import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import services from "../../../services/services"
import { Urls } from "../../../services/Urls"
import { DateObject } from "react-multi-date-picker"
import { useLocation } from "react-router-dom"
import { dateHoverRange } from "../../../utils/Helper"
import { callautopopulate } from "../../../services/callautopop"
import _ from "underscore"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

export const useProjectTimesheetDetails = () => {
    const [options, setOptions] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [timesheetDetails, setTimesheetDetails] = useState([]);
    const [loader, setLoader] = useState(false)
    const gridRef = useRef();
    const tagRef = useRef();
    const [filterValue, setFilterValue] = useState({
        hoveredRange: [
            new DateObject().subtract(7, 'days'),
            new DateObject().add(7, 'days'),
        ],
        isAPI: false,
    })
    const [expandedDates, setExpandedDates] = useState(new Set());
    const { state } = useLocation();
    useEffect(() => {
        callautopopulate("users", "projects", "project_leads","user_info").then((res) => {
            setOptions({
                userOption: _.sortBy(res?.users, "user_display_name"),
                projectOption: _.sortBy(res?.projects, "project_name"),
                leadOption: _.sortBy(res?.project_leads, "user_display_name")
            })
            const { project_id } = state || {};
            const match = res?.projects.find(o => o.project_id === project_id);
            setFilterValue(prev => ({
                ...prev,
                isAPI: true,
                projects: project_id && match ? [match] : [],
                leads:_.filter(res?.project_leads, { user_code: res?.user_info?.[0]?.user_code })
            }));
        })
    }, [state])

    useEffect(() => {
        filterValue?.apiCall && getProjectWiseTimesheetSummary();
    }, [filterValue?.apiCall])

    const transformDataForGrid = useCallback((rawData) => {
        if (!rawData?.length) return [];

        const dateGroups = {};
        rawData.forEach(entry => {
            const date = entry.day_date;
            if (!dateGroups[date]) dateGroups[date] = [];
            dateGroups[date].push(entry);
        });

        const result = [];

        Object.keys(dateGroups)
            .sort((a, b) => new Date(a) - new Date(b))
            .forEach(date => {
                const formattedDate = new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric'
                });

                const totalHours = dateGroups[date].reduce((sum, entry) =>
                    sum + parseFloat(entry.timeSpent || 0), 0
                );

                const isExpanded = expandedDates.has(date);
                result.push({
                    rowType: 'date-header',
                    date: formattedDate,
                    day_date: date,
                    displayDate: formattedDate,
                    totalHours,
                    isExpanded,
                    userCount: Object.keys(
                        dateGroups[date].reduce((users, entry) => {
                            users[entry.user_display_name] = true;
                            return users;
                        }, {})
                    ).length
                });

                if (isExpanded) {
                    const userGroups = {};
                    dateGroups[date].forEach(entry => {
                        const user = entry.user_display_name;
                        if (!userGroups[user]) {
                            userGroups[user] = [];
                        }
                        userGroups[user].push(entry);
                    });

                    Object.keys(userGroups)
                        .sort()
                        .forEach(userName => {
                            const userEntries = userGroups[userName];
                            result.push({
                                rowType: 'user-header',
                                user_display_name: userName,
                                day_date: date,
                                displayDate: formattedDate,
                                userTotalHours: userEntries.reduce((sum, entry) =>
                                    sum + parseFloat(entry.timeSpent || 0), 0
                                )
                            });

                            userEntries.sort((a, b) => {
                                const timeA = a.log_datetime || '00:00:00';
                                const timeB = b.log_datetime || '00:00:00';
                                return timeA.localeCompare(timeB);
                            }).forEach(entry => {
                                result.push({
                                    rowType: 'user-entry',
                                    day_date: date,
                                    displayDate: formattedDate,
                                    issue_type: entry.issue_type_name || '-',
                                    issue_key: entry.issue_key || '',
                                    issue_summary: entry.issue_summary || '',
                                    issue_url: entry.issue_url || '',
                                    comment: entry.comment || '',
                                    timeSpent: parseFloat(entry.timeSpent || 0),
                                    log_time: entry.log_time || '',
                                    project_name: entry.project_name || ''
                                });
                            });
                        });
                }
            });

        return result;
    }, [expandedDates]);

    const toggleDateExpansion = useCallback((date) => {
        setExpandedDates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(date)) {
                newSet.delete(date);
            } else {
                newSet.add(date);
            }
            return newSet;
        });
    }, []);

    const createDynamicColumns = useCallback(() => {
        return [
            {
                field: 'displayDate',
                headerName: 'Date',
                pinned: 'left',
                width: 200,
                cellRenderer: (params) => {
                    if (params.data.rowType !== 'date-header') return null;
                    const { date, day_date, isExpanded } = params.data;
                    return (
                        <div className="c-pointer d-flex gap-2" onClick={() => toggleDateExpansion(day_date)}>
                            <div className="fw-semibold">
                                <span> {isExpanded ? <FontAwesomeIcon icon="fa-solid fa-angle-up" /> : <FontAwesomeIcon icon="fa-solid fa-angle-down" />}</span>
                                <span> {date}</span>
                            </div>
                        </div>
                    );
                }
            },
            {
                field: 'user_display_name',
                headerName: 'User',
                pinned: 'left',
                width: 170,
                cellRenderer: (params) => {
                    if (params.data.rowType === 'date-header') return null;

                    const isUserHeader = params.data.rowType === 'user-header';
                    if (isUserHeader) {
                        return (
                            <div style={{ 
                            }}>
                                {params.value}
                                <span style={{
                                   
                                }}>
                                    ({params.data.userTotalHours?.toFixed(2)}h)
                                </span>
                            </div>
                        );
                    }
                    return <div style={{ paddingLeft: '24px', minHeight: '24px' }}></div>;
                }
            },
            {
                headerName: 'Comment',
                field: 'comment',
                minWidth: 640,
                wrapText: true,
                autoHeight: true,
                cellRenderer: (params) => {
                    if (params.data.rowType !== 'user-entry') return null;
                    const comment = params.data.comment || '-';
                    const issueKey = params.data.issue_key;
                    const issueUrl = params.data.issue_url;
                    const issueType = params.data.issue_type;
                    const handleClick = (e) => {
                        e.stopPropagation();
                        if (issueUrl) {
                            window.open(issueUrl, '_blank', 'noopener,noreferrer');
                        }
                    };
                    return (
                        <div title={comment}>
                            {
                                issueKey && <span
                                    style={{
                                        color: '#007bff',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                    onClick={handleClick}
                                    title={issueUrl || issueKey}
                                >
                                    {issueKey}
                                </span>
                            }  {issueType && <span>({issueType})</span>}{comment}
                        </div>
                    );
                }
            },
            {
                headerName: 'Hours',
                field: 'timeSpent',
                width: 90,
                cellRenderer: (params) => {
                    if (params.data.rowType !== 'user-entry') return null;
                    return params.data.timeSpent > 0 ?
                        <div style={{ paddingLeft: '24px' }}>
                            {params.data.timeSpent.toFixed(2)}h
                        </div> :
                        <span style={{ color: '#6c757d', paddingLeft: '24px' }}>-</span>;
                }
            },
            {
                headerName: 'Log Time',
                field: 'log_time',
                width: 130,
                cellRenderer: (params) => {
                    if (params.data.rowType !== 'user-entry') return null;
                    return (
                        <div style={{ paddingLeft: '24px' }}>
                            {params.value || '-'}
                        </div>
                    );
                }
            },
            {
                headerName: 'Project',
                field: 'project_name',
                width: 150,
                cellRenderer: (params) => {
                    if (params.data.rowType !== 'user-entry') return null;
                    return (
                        <span style={{ paddingLeft: '24px' }}>
                            {params.value || '-'}
                        </span>
                    );
                }
            }
        ];
    }, [toggleDateExpansion]);

    useEffect(() => {
        const timesheetData = timesheetDetails || [];
        if (timesheetData.length > 0) {
            const newColumnDefs = createDynamicColumns();
            const newRowData = transformDataForGrid(timesheetData);
            setColumnDefs(newColumnDefs);
            setRowData(newRowData);
        }
    }, [timesheetDetails, createDynamicColumns, transformDataForGrid]);

    const getProjectWiseTimesheetSummary = async () => {
        try {
            setLoader(true)
            const payload = {
                "from_date": dateHoverRange(filterValue?.hoveredRange[0]) || "",
                "to_date": dateHoverRange(filterValue?.hoveredRange[1]) || "",
                "project_ids": _.pluck(filterValue?.projects, "project_id").toString(),
                "issuetypes": "",
                "users": _.pluck(filterValue?.users, "user_code").toString(),
                "project_lead_user_code": _.pluck(filterValue?.project_leads, "user_code").toString(),
            }
            const res = await services.getInvokeRemote("POST", Urls("GETMANAGERTIMESHEETCALENDARDETAILS"), payload)
            if (!res?.body?.error_status) {
                setTimesheetDetails(res?.body?.data?.timesheet_details)  
                setExpandedDates(new Set());
            }
            setLoader(false)
              setFilterValue((prev) => ({ ...prev, apiCall: false }))
        } catch (error) {
            setLoader(false)
              setFilterValue((prev) => ({ ...prev, apiCall: false }))
            console.error("Error fetching timesheet summary:", error)
        }
    }




    return useMemo(() => ({
        options, filterValue, setFilterValue, getProjectWiseTimesheetSummary, gridRef, columnDefs, rowData, loader, toggleDateExpansion, tagRef
    }), [options, filterValue, getProjectWiseTimesheetSummary, gridRef, columnDefs, rowData, loader, toggleDateExpansion, tagRef])
}


